import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

export default function PricingSection() {
  const { t } = useTranslation();
  const [characters, setCharacters] = useState<number>(0);
  const [languages, setLanguages] = useState<number>(0);
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 计算预估成本
  const calculateCost = (chars: number, langs: number) => {
    if (chars <= 0 || langs === 0) {
      return 0; // 没有字符或没有选择语言
    }
    
    // 免费额度：5000字符一种语言
    const freeQuota = 5000;
    
    // 总字符使用量 = 字符数 × 语言数量
    const totalCharacterUsage = chars * langs;
    
    // 如果总使用量不超过免费额度，则免费
    if (totalCharacterUsage <= freeQuota) {
      return 0;
    }
    
    // 超出免费额度的部分
    const billableChars = totalCharacterUsage - freeQuota;
    
    // 超出部分按每个字符$0.011计费
    const cost = billableChars * 0.011;
    
    return Math.max(0, cost);
  };

  useEffect(() => {
    const cost = calculateCost(characters, languages);
    setEstimatedCost(cost);
  }, [characters, languages]);

  const handleCharactersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setCharacters(value);
  };

  const handleLanguagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setLanguages(value);
  };

  return (
    <section id="pricing" className="py-20 px-6 bg-gray-950">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-indigo-400 text-xl mb-3">{t('pricingSubtitle')}</h2>
          <h3 className="text-4xl font-bold mb-6 text-white">{t('pricingTitle')}</h3>
          <p className="text-gray-300 max-w-2xl mx-auto">
            {t('pricingDescription')}
          </p>
        </div>

        {/* Free Trial Section */}
        <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl p-8 mb-12 border border-indigo-500/20">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-6">
              <i className="ri-gift-line text-white ri-2x"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">{t('freeTrialTitle')}</h3>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-gray-900/50 rounded-xl p-6">
                <div className="text-3xl font-bold text-indigo-400 mb-2">5,000</div>
                <div className="text-gray-300">{t('charactersCount')}</div>
              </div>
              <div className="bg-gray-900/50 rounded-xl p-6">
                <div className="text-3xl font-bold text-purple-400 mb-2">1</div>
                <div className="text-gray-300">{t('languagesCount')}</div>
              </div>
              <div className="bg-gray-900/50 rounded-xl p-6">
                <div className="text-3xl font-bold text-green-400 mb-2">{t('freeExperience')}</div>
                <div className="text-gray-300">{t('freeExperience')}</div>
              </div>
            </div>
            <p className="text-gray-400 mt-6 max-w-2xl mx-auto">
              {t('freeTrialDescription')}
            </p>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Standard Pricing */}
          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 hover:border-indigo-500/50 transition-all duration-300">
            <div className="text-center mb-8">
              <h4 className="text-2xl font-bold text-white mb-4">{t('standardPricing')}</h4>
              <div className="flex items-center justify-center mb-4">
                <span className="text-5xl font-bold text-white">$0.011</span>
                <div className="ml-3 text-left">
                  <div className="text-gray-400 text-sm">{t('perCharacter')}</div>
                  <div className="text-gray-400 text-sm">{t('perLanguage')}</div>
                </div>
              </div>
              <p className="text-gray-400">{t('standardPricingDescription')}</p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('standardPricingFeatures.languages')}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('standardPricingFeatures.speed')}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('standardPricingFeatures.formats')}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('standardPricingFeatures.glossary')}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('standardPricingFeatures.quality')}</span>
              </div>
            </div>

            <button 
              onClick={() => scrollToSection('contact')}
              className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 cursor-pointer"
            >
              {t('startUsing')}
            </button>
          </div>

          {/* Enterprise Pricing */}
          <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-2xl p-8 border border-indigo-500/30 relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <span className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                {t('recommended')}
              </span>
            </div>

            <div className="text-center mb-8">
              <h4 className="text-2xl font-bold text-white mb-4">{t('enterpriseCustom')}</h4>
              <div className="flex items-center justify-center mb-4">
                <span className="text-5xl font-bold text-white">{t('customPrice')}</span>
                <div className="ml-3 text-left">
                  <div className="text-gray-400 text-sm">{t('customPlan')}</div>
                </div>
              </div>
              <p className="text-gray-400">{t('customPlanDescription')}</p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('enterpriseFeatures.allStandard')}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('enterpriseFeatures.manager')}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('enterpriseFeatures.priority')}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('enterpriseFeatures.customGlossary')}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('enterpriseFeatures.discount')}</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-400 mr-3"></i>
                <span className="text-gray-300">{t('enterpriseFeatures.extraTrial')}</span>
              </div>
            </div>

            <button 
              onClick={() => scrollToSection('contact')}
              className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 cursor-pointer shadow-lg shadow-indigo-500/25"
            >
              {t('contactBusinessTeam')}
            </button>
          </div>
        </div>

        {/* Pricing Calculator */}
        <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
          <div className="text-center mb-8">
            <h4 className="text-2xl font-bold text-white mb-4">{t('priceCalculator')}</h4>
            <p className="text-gray-400">{t('calculatorDescription')}</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('charactersCount')}</label>
                <input
                  type="number"
                  id="characters"
                  placeholder="100000"
                  value={characters || ''}
                  onChange={handleCharactersChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-5
                    00 focus:border-transparent transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('targetLanguagesCount')}</label>
                <input
                  type="number"
                  id="languages"
                  placeholder="5"
                  value={languages || ''}
                  onChange={handleLanguagesChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('estimatedCost')}</label>
                <div className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white flex items-center">
                  <span id="estimated-cost" className="text-indigo-400 font-bold">
                    ${estimatedCost.toFixed(3)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-information-line text-blue-400"></i>
                </div>
                <div>
                  <h5 className="font-medium text-white mb-2">{t('calculationNotes')}</h5>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• {t('calculationDetails.freeQuota')}</li>
                    <li>• {t('calculationDetails.pricing')}</li>
                    <li>• {t('calculationDetails.bulkDiscount')}</li>
                    <li>• {t('calculationDetails.finalPrice')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Link */}
        <div className="text-center mt-12">
          <p className="text-gray-400 mb-4">{t('stillHaveQuestions')}</p>
          <button 
            onClick={() => scrollToSection('contact')}
            className="inline-flex items-center text-indigo-400 hover:text-purple-400 transition-colors cursor-pointer"
          >
            {t('viewFaq')}
            <i className="ri-arrow-right-line ri-sm ml-2"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
